import { Popover, PopoverContent, PopoverTrigger } from "@capacit-com/lara-law-shared-ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { HelpCircle } from "lucide-react";
import { useState } from "react";
import { SearchTag } from "./search-tag";
import { cn } from "@/lib/utils";

interface SearchSyntaxHelperProps {
  buttonClassName?: string;
  popoverAlign?: "center" | "start" | "end";
  isSearchBar?: boolean;
}
const SearchSyntaxHelper = ({
  buttonClassName,
  popoverAlign = "center",
  isSearchBar = false,
}: SearchSyntaxHelperProps) => {
  const [isSearchHelpOpen, setIsSearchHelpOpen] = useState(false);
  return (
    <>
      <Popover open={isSearchHelpOpen} onOpenChange={setIsSearchHelpOpen}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    buttonClassName ??
                      "absolute right-1 size-6 p-0 text-muted-foreground hover:text-foreground"
                  )}
                >
                  <HelpCircle className="size-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            {!isSearchHelpOpen && (
              <TooltipContent side="bottom">
                <p>Læs mere om avanceret søgesyntaks</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <PopoverContent
          className="w-fit p-4"
          align={popoverAlign}
          side="bottom"
        >
          <div className="space-y-4">
            <div>
              <h4
                className={cn("font-semibold text-sm", !isSearchBar && "mb-2")}
              >
                Avanceret søgesyntaks
              </h4>
              {isSearchBar && (
                <div className="text-muted-foreground text-xs mb-3 max-w-[420px] space-y-2">
                  <p className="leading-relaxed">
                    Søgninger i søgefeltet og tags vises efter relevans, hvor de
                    mest relevante resultater vises først.
                  </p>
                  <p className="leading-relaxed">
                    Søgningen søger både på titler og tekster i dokumenter for
                    både kilder, workspaces og workspace dokumenter, samt titler
                    og beskeder i chats.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-[220px_auto_auto] gap-x-4 gap-y-2 items-center">
                <code className="font-mono bg-muted px-2 py-1 rounded text-xs justify-self-start">
                  kontrakt brud
                </code>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  finder begge ord
                </span>

                <code className="font-mono bg-muted px-2 py-1 rounded text-xs justify-self-start">
                  &quot;brud på kontrakt&quot;
                </code>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  finder den eksakte sætning
                </span>

                <code className="font-mono bg-muted px-2 py-1 rounded text-xs justify-self-start">
                  kontrakt -ansættelse
                </code>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  udelukker et ord
                </span>

                <code className="font-mono bg-muted px-2 py-1 rounded text-xs justify-self-start">
                  kontrakt -&quot;brud på kontrakt&quot;
                </code>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  udelukker en sætning
                </span>
              </div>
            </div>

            <div>
              <div className="flex flex-col">
                <h4
                  className={cn("font-semibold text-sm", isSearchBar && "mb-3")}
                >
                  Eksempler på tags
                </h4>

                {!isSearchBar && (
                  <p className="text-muted-foreground text-xs mb-3">
                    Både en søgning i søgefeltet, samt tags ingår i selve
                    søgningen.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-[220px_auto_auto] gap-x-4 gap-y-2 items-center">
                <div className="flex items-center gap-1.5 justify-self-start">
                  <SearchTag tag="kontrakt" onRemove={() => {}} />
                  <SearchTag tag="brud" onRemove={() => {}} />
                </div>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  finder begge ord
                </span>

                <div className="justify-self-start">
                  <SearchTag tag="kontrakt brud" onRemove={() => {}} />
                </div>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  finder den eksakte sætning
                </span>

                <div className="justify-self-start">
                  <SearchTag tag="-kontrakt" onRemove={() => {}} />
                </div>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  udelukker et ord
                </span>

                <div className="justify-self-start">
                  <SearchTag tag="-brud på kontrakt" onRemove={() => {}} />
                </div>
                <span className="text-muted-foreground text-xs">→</span>
                <span className="text-muted-foreground text-xs">
                  udelukker en sætning
                </span>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default SearchSyntaxHelper;
